<script lang="ts" setup>
import { computed } from 'vue'

import { Figure, Figcaption, Picture, PictureWithBlur } from '@toni/brand-ui'

import { usePageStore } from '../../../stores/page'
import { useConfigStore } from '../../../stores/config'
import { ImageContentBlock } from '../../../utils/content-block-schema'
import { toValidHTML } from '../../../utils/html-utils'
import { createImgSrc, getImgDefinitions, createImgSrcSetArray, isPortraitImage, setMediaQuery } from '../../bauer-image/image-utils'

const props = defineProps<{
  image: ImageContentBlock['image']
}>()

const pageStore = usePageStore()
const brand = computed(() => pageStore.brandFromStore as string)
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
// Get setName for portrait images from imageSrcDefinitions in constants
const setName = 'image-l' as string
const imageDefinitions = getImgDefinitions(brand.value, imageSrcDefinitions, setName)
const imgFallbackWidth = imageDefinitions.maxWidth ?? imageSrcDefinitions?.maxWidth ?? 0

type Crop = {
  x1?: string;
  y1?: string;
  x2?: string;
  y2?: string;
}

interface FormattedGalleryItem {
  imageId: string,
  alt: string,
  title: string,
  html: string,
  imageCredit: string,
  width?: number,
  height?: number,
  crop?: Crop
}

const item = computed<FormattedGalleryItem | null>(() => {
  if (!props.image) return null

  return {
    imageId: props.image.imageId ?? '',
    alt: props.image.imageAltText ?? '',
    title: props.image.imageTitle ?? '',
    html: props.image.imageDescription?.html ?? '',
    imageCredit: props.image.imageCredit ?? '',
    width: props.image.width,
    height: props.image.height,
    crop: props.image.crop
  }
})

const isItemPortrait = (item: FormattedGalleryItem) => {
  return isPortraitImage(item.width || 0, item.height || 0)
}

const imgSrc = (item: FormattedGalleryItem) => {
  if (!item) return undefined

  const portrait = isItemPortrait(item)

  if (portrait) {
    const maxHeight = 772
    const aspectRatio = (item.width || 1) / (item.height || 1)
    const portraitWidth = Math.round(maxHeight * aspectRatio)

    return (createImgSrc({
      image: item,
      brand: brand.value,
      imageServiceUrl
    }, portraitWidth))?.href ?? ''
  }

  return (createImgSrc({
    image: item,
    brand: brand.value,
    imageServiceUrl,
    imageSrcDefinitions,
    setName,
    srcSetDefinition: {
      format: 'landscape'
    }
  }, imgFallbackWidth))?.href ?? ''
}

const imgSrcSetArray = (item: FormattedGalleryItem) => {
  return createImgSrcSetArray({
    image: item,
    brand: brand.value,
    imageServiceUrl,
    imageSrcDefinitions,
    setName
  })
}

const blurredBgSrc = () => {
  if (!item.value?.imageId) return undefined

  return (createImgSrc({
    image: item.value,
    brand: brand.value,
    imageServiceUrl,
    setName,
    srcSetDefinition: {
      format: 'landscape'
    }
  }, imgFallbackWidth))?.href ?? ''
}
</script>

<template>
  <Figure class="flex flex-col">
    <Picture :class="item && isItemPortrait(item) ? 'absolute top-0 flex justify-center items-center w-full aspect-4/3 md:aspect-16/9 z-10' : 'w-full aspect-4/3 md:aspect-16/9'">
      <source v-for="(singleSrc, index) in item ? imgSrcSetArray(item) : []"
              :key="index + 'webp'"
              type="image/webp"
              :media="setMediaQuery(singleSrc.viewportWidth)"
              :srcset="singleSrc.src">
      <img :src="item ? imgSrc(item) : ''"
           :alt="item?.alt"
           :title="item?.title"
           :class="item && isItemPortrait(item) ? 'h-full max-h-full max-w-full object-contain' : 'w-full object-cover rounded-image aspect-4/3 md:aspect-16/9'">
    </Picture>
    <PictureWithBlur v-if="item && isItemPortrait(item)">
      <img :src="blurredBgSrc() ?? ''"
           alt=""
           class="w-full h-full object-cover filter blur-md scale-110">
    </PictureWithBlur>
    <Figcaption v-if="item?.html || item?.imageCredit"
                class="mt-2">
      <div v-if="item.html"
           class="text-ui-sm mb-2"
           v-html="toValidHTML(item.html)" />
      <p v-if="item.imageCredit"
         class="text-ui-xs">
        © {{ item.imageCredit }}
      </p>
    </Figcaption>
  </Figure>
</template>
